import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Upload,
  Package,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  ImageIcon,
  X,
  Camera,
  Info,
  Check,
  File,
  Palette,
  Type,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import productService from "@/services/productService";
import { formatCurrency } from "@/lib/utils";
import DigitalFileUpload from "@/components/admin/DigitalFileUpload";
import type { DigitalFileUpload as DigitalFileType } from "@/services/digitalFileService";
import MultiImageUpload from "@/components/admin/MultiImageUpload";
import { estampadosService } from "@/services/estampadosService";
import { hojasService } from "@/services/hojasService";
import { categoriasService } from "@/services/categoriasService";
import type { Estampado, HojaInterna } from "@/components/personalizable/types";
import {
  EditorTamanos,
  type OpcionTamano,
} from "@/components/admin/EditorTamanos";
import { EditorPersonalizacion } from "@/components/admin/EditorPersonalizacion";

interface Product {
  _id: string;
  title: string;
  description?: string;
  type: "físico" | "digital" | "taller";
  price: number;
  stock?: number;
  tallerInfo?: {
    fechaEvento?: string;
    lugar?: string;
    duracion?: string;
    capacidadMaxima?: number;
    inscripcionesActivas?: boolean;
  };
  image: string; // Deprecado
  images: string[];
  orientacion?: 'horizontal' | 'vertical';
  tags: string[];
  isActive: boolean;
  digitalFileId?: string;
  createdAt: string;
  soldCount?: number;
  esPersonalizable?: boolean;
  permiteNombrePersonalizado?: boolean;
  featuredOrder?: number; // Orden de destacado (menor = más prioritario)
  opcionesTamano?: OpcionTamano[]; // Tamaños ESTÁNDAR
  configuracionPersonalizable?: {
    hojasDisponibles?: string[];
    personalizacion?: {
      categoriaEstampados?: string;
      permiteEstampados?: boolean;
      permiteImagenPersonalizada?: boolean;
      campoNombre?: {
        activo: boolean;
        label: string;
        placeholder: string;
        maxCaracteres: number;
      };
      opcionesTamano?: OpcionTamano[]; // Tamaños PERSONALIZABLES
    };
  };
}

interface ProductFilters {
  search?: string;
  type?: "físico" | "digital" | "taller" | "";
  isActive?: boolean | "";
  page?: number;
  limit?: number;
}

interface ProductFormData {
  title: string;
  description: string;
  type: "físico" | "digital" | "taller";
  price: number;
  stock: number;
  tallerInfo?: {
    fechaEvento?: string;
    lugar?: string;
    duracion?: string;
    capacidadMaxima?: number;
    inscripcionesActivas?: boolean;
  };
  image?: string; // Deprecado
  images: string[];
  orientacion?: 'horizontal' | 'vertical';
  tags: string[];
  digitalFileId?: string;
  isActive: boolean;
  esPersonalizable?: boolean;
  permiteNombrePersonalizado?: boolean;
  slug?: string; // Slug para URL personalizable
  featuredOrder?: number; // Orden de destacado
  opcionesTamano?: OpcionTamano[]; // Tamaños ESTÁNDAR
  configuracionPersonalizable?: {
    hojasDisponibles?: string[];
    personalizacion?: {
      categoriaEstampados?: string;
      permiteEstampados?: boolean;
      permiteImagenPersonalizada?: boolean;
      campoNombre?: {
        activo: boolean;
        label: string;
        placeholder: string;
        maxCaracteres: number;
      };
      opcionesTamano?: OpcionTamano[]; // Tamaños PERSONALIZABLES
    };
  };
}

interface ImageUploadProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const ImageUpload = ({ value, onChange, className }: ImageUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    setError("");

    // Validar tipo de archivo
    if (!file.type.startsWith("image/")) {
      setError("Por favor selecciona un archivo de imagen válido");
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("La imagen debe ser menor a 5MB");
      return;
    }

    setIsLoading(true);

    // Crear URL temporal para preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      onChange(result);
      setIsLoading(false);
    };
    reader.onerror = () => {
      setError("Error al leer el archivo");
      setIsLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUrlChange = (url: string) => {
    setError("");
    onChange(url);
  };

  const removeImage = () => {
    onChange("");
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={className}>
      <div className="space-y-4">
        {/* Preview de la imagen */}
        {value && (
          <div className="relative">
            <img
              src={value}
              alt="Preview"
              className="w-full h-48 object-cover rounded-lg border"
              onError={() => setError("Error al cargar la imagen desde la URL")}
            />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2"
              onClick={removeImage}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Área de drop */}
        <div
          className={`
            border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
            ${
              isDragging
                ? "border-primary bg-primary/5"
                : "border-gray-300 hover:border-gray-400"
            }
            ${value ? "h-32" : "h-48"}
          `}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
          />

          {isLoading ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-sm text-muted-foreground mt-2">
                Procesando imagen...
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm font-medium">
                {value
                  ? "Cambiar imagen"
                  : "Arrastra una imagen aquí o haz clic para seleccionar"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG, WEBP hasta 5MB
              </p>
            </div>
          )}
        </div>

        {/* Input manual de URL */}
        <div className="space-y-2">
          <label className="text-sm font-medium">
            O ingresa una URL de imagen:
          </label>
          <Input
            type="url"
            placeholder="https://ejemplo.com/imagen.jpg"
            value={value.startsWith("data:") ? "" : value}
            onChange={(e) => handleUrlChange(e.target.value)}
          />
        </div>

        {/* Guías de imagen */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <h4 className="font-medium text-blue-900 mb-2">
                Recomendaciones para la imagen:
              </h4>
              <ul className="space-y-1 text-blue-800">
                <li>
                  • <strong>Resolución:</strong> Mínimo 800x800px, ideal
                  1200x1200px
                </li>
                <li>
                  • <strong>Formato:</strong> JPG, PNG o WEBP
                </li>
                <li>
                  • <strong>Proporción:</strong> Cuadrada (1:1) o rectangular
                  (4:3)
                </li>
                <li>
                  • <strong>Tamaño:</strong> Máximo 5MB
                </li>
                <li>
                  • <strong>Calidad:</strong> Imagen clara y bien iluminada
                </li>
                <li>
                  • <strong>Fondo:</strong> Preferible fondo blanco o neutro
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ProductForm = ({
  product,
  onSubmit,
  onCancel,
  isLoading,
}: {
  product?: Product | null;
  onSubmit: (data: ProductFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ProductFormData>({
    title: product?.title || "",
    description: product?.description || "",
    type: product?.type || "físico",
    price: product?.price || 0,
    stock: product?.stock || 0,
    images:
      product?.images?.length > 0
        ? product.images
        : product?.image
        ? [product.image]
        : [],
    orientacion: product?.orientacion || 'vertical',
    tags: product?.tags || [],
    digitalFileId: product?.digitalFileId || "",
    isActive: product?.isActive ?? true,
    featuredOrder: product?.featuredOrder,
    permiteNombrePersonalizado: product?.permiteNombrePersonalizado || false,
    opcionesTamano: product?.opcionesTamano || [],
    configuracionPersonalizable: product?.configuracionPersonalizable
      ? {
          ...product.configuracionPersonalizable,
          personalizacion: product.configuracionPersonalizable.personalizacion
            ? {
                ...product.configuracionPersonalizable.personalizacion,
                categoriaEstampados:
                  product.configuracionPersonalizable.personalizacion
                    .categoriaEstampados || "infantil",
              }
            : undefined,
        }
      : undefined,
  });

  const [tagInput, setTagInput] = useState("");
  const [esPersonalizable, setEsPersonalizable] = useState(
    !!product?.configuracionPersonalizable?.personalizacion ||
      !!product?.configuracionPersonalizable?.hojasDisponibles?.length
  );
  const [categoriaHojas, setCategoriaHojas] = useState<string>(
    (product?.configuracionPersonalizable?.personalizacion
      ?.categoriaEstampados as string) || "infantil"
  );
  const { data: categoriasHojaData } = useQuery({
    queryKey: ["categorias", "estampado"],
    queryFn: () => categoriasService.getCategorias("estampado"),
    staleTime: 1000 * 60 * 5,
  });
  const categoriasDisponibles = (categoriasHojaData ?? []).map((c) => ({
    value: c.slug,
    label: `${c.label}`,
    emoji: c.emoji,
  }));

  // Sincronizar: si la categoría activa no está en la lista cargada, usar la primera disponible
  useEffect(() => {
    if (
      categoriasDisponibles.length > 0 &&
      !categoriasDisponibles.some((c) => c.value === categoriaHojas)
    ) {
      setCategoriaHojas(categoriasDisponibles[0].value);
    }
  }, [categoriasDisponibles, categoriaHojas]);

  const [mostrarCreadorCategoria, setMostrarCreadorCategoria] = useState(false);
  const [nuevaCategoria, setNuevaCategoria] = useState({
    nombre: "",
    emoji: "📁",
  });
  const [mostrarCreadorHoja, setMostrarCreadorHoja] = useState(false);
  const [nuevaHoja, setNuevaHoja] = useState({
    nombre: "",
    descripcion: "",
    orden: 0,
    archivo: null as File | null,
    preview: null as string | null,
  });

  // Query para cargar las hojas INTERNAS disponibles según la categoría
  const { data: hojasDisponibles, isLoading: isLoadingHojas } = useQuery({
    queryKey: ["hojas-internas", categoriaHojas],
    queryFn: () => hojasService.getHojasPorCategoria(categoriaHojas),
    enabled: esPersonalizable,
  });

  // Query para cargar los ESTAMPADOS disponibles según la categoría
  const { data: estampadosDisponibles, isLoading: isLoadingEstampados } = useQuery({
    queryKey: ["estampados", categoriaHojas],
    queryFn: () => estampadosService.getEstampadosPorCategoria(categoriaHojas as 'infantil' | 'literario' | 'general'),
    enabled: esPersonalizable && (formData.configuracionPersonalizable?.personalizacion?.permiteEstampados ?? true),
  });

  // Mutation para crear nueva hoja
  const crearHojaMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      return hojasService.crearHoja(formData);
    },
    onSuccess: (hojaCreada) => {
      toast({
        title: "Hoja creada exitosamente",
        description: `La hoja "${hojaCreada.nombre}" se creó correctamente`,
      });
      // Refrescar lista de hojas
      queryClient.invalidateQueries({
        queryKey: ["hojas-internas", categoriaHojas],
      });
      // Limpiar formulario
      setNuevaHoja({
        nombre: "",
        descripcion: "",
        orden: 0,
        archivo: null,
        preview: null,
      });
      setMostrarCreadorHoja(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error al crear hoja",
        description: error.response?.data?.error || error.message,
        variant: "destructive",
      });
    },
  });

  // Sincronizar formData cuando cambie el producto
  useEffect(() => {
    if (product) {
      setFormData({
        title: product.title || "",
        description: product.description || "",
        type: product.type || "físico",
        price: product.price || 0,
        stock: product.stock || 0,
        images:
          product?.images?.length > 0
            ? product.images
            : product?.image
            ? [product.image]
            : [],
        orientacion: product.orientacion || 'vertical',
        tags: product.tags || [],
        digitalFileId: product.digitalFileId || "",
        isActive: product.isActive ?? true,
        esPersonalizable: product.esPersonalizable || false,
        permiteNombrePersonalizado: product.permiteNombrePersonalizado || false,
        slug: product.slug || undefined,
        featuredOrder: product.featuredOrder,
        opcionesTamano: product.opcionesTamano || [],
        configuracionPersonalizable: product.configuracionPersonalizable
          ? {
              ...product.configuracionPersonalizable,
              personalizacion: product.configuracionPersonalizable.personalizacion
                ? {
                    ...product.configuracionPersonalizable.personalizacion,
                    categoriaEstampados:
                      product.configuracionPersonalizable.personalizacion
                        .categoriaEstampados || "infantil",
                  }
                : undefined,
            }
          : undefined,
        tallerInfo: product.tallerInfo || undefined,
      });

      // Actualizar estados de personalización
      setEsPersonalizable(
        !!product.configuracionPersonalizable?.personalizacion ||
          !!product.configuracionPersonalizable?.hojasDisponibles?.length
      );
      const categoriaProducto =
        (product.configuracionPersonalizable?.personalizacion
          ?.categoriaEstampados as string) || "infantil";
      setCategoriaHojas(categoriaProducto);

    } else {
      // Reset para crear nuevo producto
      setFormData({
        title: "",
        description: "",
        type: "físico",
        price: 0,
        stock: 0,
        images: [],
        orientacion: 'vertical',
        tags: [],
        digitalFileId: "",
        isActive: true,
        esPersonalizable: false,
        permiteNombrePersonalizado: false,
        slug: undefined,
        featuredOrder: undefined,
        opcionesTamano: [],
        configuracionPersonalizable: undefined,
      });

      setEsPersonalizable(false);
      setCategoriaHojas("infantil");
    }
  }, [product]);

  const handleDigitalFileSelected = (file: DigitalFileType | null) => {
    setFormData((prev) => ({
      ...prev,
      digitalFileId: file?.fileId || "",
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Generar slug automáticamente si es personalizable y no tiene slug
    let dataToSubmit = { ...formData };
    if (esPersonalizable && !dataToSubmit.slug) {
      const slug = formData.title
        .toLowerCase()
        .trim()
        .replace(/[áàäâ]/g, 'a')
        .replace(/[éèëê]/g, 'e')
        .replace(/[íìïî]/g, 'i')
        .replace(/[óòöô]/g, 'o')
        .replace(/[úùüû]/g, 'u')
        .replace(/ñ/g, 'n')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      dataToSubmit.slug = slug;
    }

    // Asegurar que esPersonalizable se envíe correctamente
    dataToSubmit.esPersonalizable = esPersonalizable;

    console.log('📋 [DEBUG ProductForm] Guardando producto con configuración:', {
      esPersonalizable: dataToSubmit.esPersonalizable,
      slug: dataToSubmit.slug,
      fullPersonalizacion: dataToSubmit.configuracionPersonalizable?.personalizacion
    });
    console.log('📋 [DEBUG ProductForm] formData.configuracionPersonalizable completo:', dataToSubmit.configuracionPersonalizable);

    onSubmit(dataToSubmit);
  };

  const addTag = () => {
    if (
      tagInput.trim() &&
      !formData.tags.includes(tagInput.trim().toLowerCase())
    ) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim().toLowerCase()],
      }));
      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const handlePersonalizableChange = (checked: boolean) => {
    setEsPersonalizable(checked);
    if (!checked) {
      // Si se desactiva, limpiar configuración personalizable
      setFormData((prev) => ({
        ...prev,
        configuracionPersonalizable: undefined,
      }));
    } else {
      // Si se activa, inicializar con estructura completa (igual que productos existentes)
      setFormData((prev) => ({
        ...prev,
        configuracionPersonalizable: {
          hojasDisponibles: [],
          personalizacion: {
            categoriaEstampados: categoriaHojas,
            permiteEstampados: true,
            permiteImagenPersonalizada: true,
            campoNombre: {
              activo: true,
              label: 'Nombre personalizado',
              placeholder: 'Ingresa un nombre',
              maxCaracteres: 50
            },
            cubierta: {
              tieneArgollas: true,
              urlConArgollas: 'https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/general/agenda-base-con-argollas.png',
              urlSinArgollas: 'https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/general/agenda-base-sin-argollas.png'
            },
            opcionesTamano: []
          },
        },
      }));
    }
  };

  const handleCategoriaChange = (nuevaCat: string) => {
    setCategoriaHojas(nuevaCat);
    // Limpiar hojas seleccionadas al cambiar categoría
    setFormData((prev) => ({
      ...prev,
      configuracionPersonalizable: {
        ...prev.configuracionPersonalizable,
        hojasDisponibles: [],
        personalizacion: {
          ...prev.configuracionPersonalizable?.personalizacion,
          categoriaEstampados: nuevaCat as "infantil" | "literario" | "general",
        },
      },
    }));
    // Cerrar el formulario de creación de hojas si está abierto
    setMostrarCreadorHoja(false);
    setNuevaHoja({
      nombre: "",
      descripcion: "",
      orden: 0,
      archivo: null,
      preview: null,
    });
  };

  const handleAgregarCategoria = async () => {
    const nombreNormalizado = nuevaCategoria.nombre
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");

    if (!nombreNormalizado) {
      toast({
        title: "Nombre requerido",
        description: "Debes ingresar un nombre para la categoría",
        variant: "destructive",
      });
      return;
    }

    // Verificar si ya existe localmente
    if (categoriasDisponibles.some((cat) => cat.value === nombreNormalizado)) {
      toast({
        title: "Categoría duplicada",
        description: "Ya existe una categoría con ese nombre",
        variant: "destructive",
      });
      return;
    }

    try {
      await categoriasService.crearCategoria({
        tipo: "estampado",
        slug: nombreNormalizado,
        label: nuevaCategoria.nombre.trim(),
        emoji: nuevaCategoria.emoji,
      });

      await queryClient.invalidateQueries({ queryKey: ["categorias", "estampado"] });
      setCategoriaHojas(nombreNormalizado);

      toast({
        title: "Categoría creada",
        description: `La categoría "${nuevaCategoria.nombre}" se creó exitosamente`,
      });
    } catch {
      toast({
        title: "Error al crear categoría",
        description: "Verificá que el nombre no esté duplicado en el sistema",
        variant: "destructive",
      });
      return;
    }

    // Limpiar y cerrar
    setNuevaCategoria({ nombre: "", emoji: "📁" });
    setMostrarCreadorCategoria(false);

    // Actualizar configuración del producto
    setFormData((prev) => ({
      ...prev,
      configuracionPersonalizable: {
        ...prev.configuracionPersonalizable,
        hojasDisponibles: [],
        personalizacion: {
          ...prev.configuracionPersonalizable?.personalizacion,
          categoriaEstampados: nombreNormalizado as any,
        },
      },
    }));
  };

  const handleHojaToggle = (hojaId: string) => {
    setFormData((prev) => {
      const hojasActuales =
        prev.configuracionPersonalizable?.hojasDisponibles || [];
      const yaSeleccionada = hojasActuales.includes(hojaId);

      return {
        ...prev,
        configuracionPersonalizable: {
          ...prev.configuracionPersonalizable,
          hojasDisponibles: yaSeleccionada
            ? hojasActuales.filter((id) => id !== hojaId)
            : [...hojasActuales, hojaId],
          personalizacion: {
            ...prev.configuracionPersonalizable?.personalizacion,
            categoriaEstampados: categoriaHojas,
          },
        },
      };
    });
  };

  const handleArchivoHojaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Archivo inválido",
        description: "Solo se permiten imágenes JPG/PNG",
        variant: "destructive",
      });
      return;
    }

    // Validar tamaño (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Archivo muy grande",
        description: "El archivo debe ser menor a 5MB",
        variant: "destructive",
      });
      return;
    }

    // Crear preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setNuevaHoja((prev) => ({
        ...prev,
        archivo: file,
        preview: event.target?.result as string,
        nombre: prev.nombre || file.name.replace(/\.[^/.]+$/, ""),
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleCrearHoja = async () => {
    if (!nuevaHoja.archivo || !nuevaHoja.nombre) {
      toast({
        title: "Datos incompletos",
        description: "Debes agregar un nombre y una imagen",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.append("nombre", nuevaHoja.nombre);
    formData.append("descripcion", nuevaHoja.descripcion);
    formData.append("categoria", categoriaHojas);
    formData.append("orden", nuevaHoja.orden.toString());
    formData.append("image", nuevaHoja.archivo);

    crearHojaMutation.mutate(formData);
  };

  return (
    <div className="space-y-6">
      {/* Header con información del tipo de producto */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-center gap-3">
          <Package className="h-6 w-6 text-blue-600" />
          <div>
            <h3 className="font-semibold text-blue-900">
              {product ? "Editar Producto" : "Crear Nuevo Producto"}
            </h3>
            <p className="text-sm text-blue-700">
              {formData.type === "digital"
                ? "Producto digital: Requiere un archivo PDF o ZIP para descarga"
                : formData.type === "taller"
                  ? "Taller: Los clientes se inscriben con sus datos — no requiere stock ni envío"
                  : "Producto físico: Requiere gestión de inventario y envío"}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Información básica */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Información Básica
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Título del Producto
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="Ingresa el nombre del producto"
                  className="text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Tipo de Producto
                </label>
                <Select
                  value={formData.type}
                  onValueChange={(value: "físico" | "digital" | "taller") =>
                    setFormData((prev) => ({
                      ...prev,
                      type: value,
                      digitalFileId:
                        value === "físico" || value === "taller" ? "" : prev.digitalFileId,
                    }))
                  }
                >
                  <SelectTrigger className="text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="físico">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Producto Físico
                      </div>
                    </SelectItem>
                    <SelectItem value="digital">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4" />
                        Producto Digital
                      </div>
                    </SelectItem>
                    <SelectItem value="taller">
                      <div className="flex items-center gap-2">
                        <Palette className="h-4 w-4" />
                        Taller
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Precio Base (USD) {esPersonalizable && <span className="text-xs text-muted-foreground font-normal">(Opcional si configuras tamaños)</span>}
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        price: parseFloat(e.target.value) || 0,
                      }))
                    }
                    placeholder="0.00"
                    className="pl-10 text-base"
                    required={!esPersonalizable}
                  />
                </div>
                {esPersonalizable && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Si no configuras tamaños más abajo, se usará este precio. Si configuras tamaños, sus precios tendrán prioridad.
                  </p>
                )}
              </div>

              {formData.type === "físico" && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Cantidad en Stock
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.stock}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        stock: parseInt(e.target.value) || 0,
                      }))
                    }
                    placeholder="0"
                    className="text-base"
                    required
                  />
                </div>
              )}
            </div>

            {/* Campos específicos de taller */}
            {formData.type === "taller" && (
              <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-900 flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Detalles del Taller (opcionales)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Fecha del evento</label>
                    <Input
                      value={formData.tallerInfo?.fechaEvento || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          tallerInfo: { ...prev.tallerInfo, fechaEvento: e.target.value },
                        }))
                      }
                      placeholder="Ej: Sábado 15 de Marzo, 2025"
                      className="text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Lugar</label>
                    <Input
                      value={formData.tallerInfo?.lugar || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          tallerInfo: { ...prev.tallerInfo, lugar: e.target.value },
                        }))
                      }
                      placeholder="Ej: Online / Ciudad de Panamá"
                      className="text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Duración</label>
                    <Input
                      value={formData.tallerInfo?.duracion || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          tallerInfo: { ...prev.tallerInfo, duracion: e.target.value },
                        }))
                      }
                      placeholder="Ej: 3 horas"
                      className="text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Capacidad máxima</label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.tallerInfo?.capacidadMaxima || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          tallerInfo: {
                            ...prev.tallerInfo,
                            capacidadMaxima: parseInt(e.target.value) || undefined,
                          },
                        }))
                      }
                      placeholder="Ej: 20"
                      className="text-base"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    id="inscripcionesActivas"
                    checked={formData.tallerInfo?.inscripcionesActivas !== false}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        tallerInfo: { ...prev.tallerInfo, inscripcionesActivas: checked },
                      }))
                    }
                  />
                  <Label htmlFor="inscripcionesActivas" className="text-sm">
                    Inscripciones abiertas
                  </Label>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
              <div className="space-y-0.5">
                <Label htmlFor="isActive" className="text-base font-medium">
                  Estado del Producto
                </Label>
                <p className="text-sm text-muted-foreground">
                  {formData.isActive
                    ? "El producto está activo y visible para los clientes"
                    : "El producto está inactivo y oculto del catálogo"}
                </p>
              </div>
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, isActive: checked }))
                }
              />
            </div>

            {/* Campo de Orden Destacado */}
            <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                  <Label
                    htmlFor="featuredOrder"
                    className="text-base font-medium text-amber-900"
                  >
                    Orden de Destacado (Opcional)
                  </Label>
                </div>
                <p className="text-sm text-amber-700">
                  Los productos con número menor aparecen primero en el catálogo
                  y landing page. Deja vacío o usa 0 para productos no
                  destacados.
                </p>
                <Input
                  id="featuredOrder"
                  type="number"
                  min="0"
                  value={formData.featuredOrder || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      featuredOrder: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    }))
                  }
                  placeholder="Ej: 1 (primer lugar), 2 (segundo), 3 (tercero)... o 0 (sin destacar)"
                  className="text-base bg-white"
                />
                <p className="text-xs text-amber-600 italic">
                  💡 Tip: Usa 1 para el producto más importante (como 🥇 primer
                  lugar), 2 para el segundo, etc. El 0 significa "sin destacar".
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Nombre Personalizado Simple */}
        {formData.type === "físico" && (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
            <div className="space-y-0.5">
              <Label
                htmlFor="permiteNombrePersonalizado"
                className="text-base font-medium flex items-center gap-2"
              >
                <Type className="h-4 w-4" />
                Permite nombre personalizado
              </Label>
              <p className="text-sm text-muted-foreground">
                {formData.permiteNombrePersonalizado
                  ? "El cliente podrá escribir un nombre sobre el producto"
                  : "Sin opción de nombre personalizado"}
              </p>
            </div>
            <Switch
              id="permiteNombrePersonalizado"
              checked={formData.permiteNombrePersonalizado || false}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({
                  ...prev,
                  permiteNombrePersonalizado: checked,
                }))
              }
            />
          </div>
        )}

        {/* Configuración de Producto Personalizable */}
        <Card className="border-2 border-primary/20">
          <CardHeader className="bg-gradient-to-br from-primary/10 to-primary/5">
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Producto Personalizable
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            {/* Switch para activar/desactivar personalización */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
              <div className="space-y-0.5">
                <Label
                  htmlFor="esPersonalizable"
                  className="text-base font-medium"
                >
                  Permitir personalización
                </Label>
                <p className="text-sm text-muted-foreground">
                  {esPersonalizable
                    ? "Este producto permite que el cliente seleccione hojas decorativas"
                    : "Este producto no permite personalización"}
                </p>
              </div>
              <Switch
                id="esPersonalizable"
                checked={esPersonalizable}
                onCheckedChange={handlePersonalizableChange}
              />
            </div>

            {/* Campo de Slug (solo visible si está activo) */}
            {esPersonalizable && (
              <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                <Label htmlFor="slug" className="text-base font-medium flex items-center gap-2">
                  🔗 URL del Producto Personalizable
                </Label>
                <p className="text-sm text-muted-foreground mt-1 mb-2">
                  Esta será la URL única para personalizar: <code className="text-xs bg-white px-1 py-0.5 rounded">/personalizar/{formData.slug || 'tu-slug'}</code>
                </p>
                <Input
                  id="slug"
                  value={formData.slug || ''}
                  onChange={(e) => {
                    const slugValue = e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, '-')
                      .replace(/--+/g, '-')
                      .replace(/^-+|-+$/g, '');
                    setFormData((prev) => ({ ...prev, slug: slugValue }));
                  }}
                  placeholder="Ej: agenda-pediatrica"
                  className="text-base font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  💡 Se genera automáticamente desde el título, pero puedes personalizarlo
                </p>
              </div>
            )}

            {/* Sección de configuración (solo visible si está activo) */}
            {esPersonalizable && (
              <div className="space-y-4 mt-4 p-4 border-2 border-dashed border-primary/30 rounded-lg bg-white">
                {/* Selector de categoría */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">
                      Categoría del producto
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setMostrarCreadorCategoria(!mostrarCreadorCategoria)
                      }
                      className="h-7 text-xs"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Nueva categoría
                    </Button>
                  </div>

                  {/* Formulario para crear categoría */}
                  {mostrarCreadorCategoria && (
                    <div className="mb-3 p-3 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50/50 space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <Input
                            placeholder="Nombre de la categoría (ej: Recetas)"
                            value={nuevaCategoria.nombre}
                            onChange={(e) =>
                              setNuevaCategoria((prev) => ({
                                ...prev,
                                nombre: e.target.value,
                              }))
                            }
                            className="text-sm h-9"
                          />
                        </div>
                        <Input
                          placeholder="📁"
                          value={nuevaCategoria.emoji}
                          onChange={(e) =>
                            setNuevaCategoria((prev) => ({
                              ...prev,
                              emoji: e.target.value,
                            }))
                          }
                          className="w-16 text-center text-sm h-9"
                          maxLength={2}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleAgregarCategoria}
                          disabled={!nuevaCategoria.nombre.trim()}
                          className="flex-1 h-8"
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Crear
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMostrarCreadorCategoria(false);
                            setNuevaCategoria({ nombre: "", emoji: "📁" });
                          }}
                          className="h-8"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}

                  <Select
                    value={categoriaHojas}
                    onValueChange={handleCategoriaChange}
                  >
                    <SelectTrigger className="text-base">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categoriasDisponibles.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.emoji} {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Define los estampados de portada y las hojas internas disponibles para este producto
                  </p>
                </div>

                {/* Vista previa de estampados disponibles para portada */}
                <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Palette className="h-5 w-5 text-blue-600" />
                    <Label className="text-sm font-semibold text-blue-900">
                      Estampados de portada disponibles en categoría "{categoriaHojas}"
                    </Label>
                  </div>
                  <p className="text-xs text-blue-700 mb-3">
                    Estos son los diseños que el cliente podrá elegir para la portada cuando personalice
                  </p>

                  {isLoadingEstampados ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <p className="ml-2 text-xs text-blue-700">Cargando estampados...</p>
                    </div>
                  ) : !estampadosDisponibles || estampadosDisponibles.length === 0 ? (
                    <div className="text-center py-4 bg-yellow-50 rounded border border-yellow-200">
                      <AlertTriangle className="h-6 w-6 mx-auto mb-1 text-yellow-600" />
                      <p className="text-xs text-yellow-800 font-medium">
                        No hay estampados en "{categoriaHojas}"
                      </p>
                      <p className="text-xs text-yellow-600 mt-1">
                        Ve a Admin → Estampados y Hojas para crear estampados
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 md:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-2 bg-white rounded border">
                      {estampadosDisponibles.map((estampado: Estampado) => (
                        <div
                          key={estampado.id}
                          className="relative group"
                          title={estampado.name}
                        >
                          <div className="aspect-square rounded overflow-hidden border-2 border-gray-200 bg-gray-50">
                            <img
                              src={estampado.path}
                              alt={estampado.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=100&h=100&fit=crop";
                              }}
                            />
                          </div>
                          <p className="text-[10px] text-center text-gray-600 line-clamp-1 mt-1">
                            {estampado.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-blue-600 mt-2 italic">
                    💡 Los estampados se gestionan desde "Admin → Estampados y Hojas"
                  </p>
                </div>

                {/* Multi-select de hojas internas */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Hojas internas incluidas en el producto (
                    {formData.configuracionPersonalizable?.hojasDisponibles
                      ?.length || 0}{" "}
                    seleccionadas)
                  </Label>

                  {isLoadingHojas ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      <p className="ml-3 text-sm text-muted-foreground">
                        Cargando hojas...
                      </p>
                    </div>
                  ) : !hojasDisponibles || hojasDisponibles.length === 0 ? (
                    <div className="space-y-4">
                      <div className="text-center py-6 bg-yellow-50 rounded-lg border border-yellow-200">
                        <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-yellow-600" />
                        <p className="text-sm text-yellow-800 font-medium">
                          No hay hojas disponibles en la categoría "
                          {categoriaHojas}"
                        </p>
                        <p className="text-xs text-yellow-600 mt-1">
                          Crea tu primera hoja para esta categoría
                        </p>
                      </div>

                      {!mostrarCreadorHoja ? (
                        <Button
                          type="button"
                          onClick={() => setMostrarCreadorHoja(true)}
                          className="w-full"
                          variant="outline"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Crear hoja en categoría "{categoriaHojas}"
                        </Button>
                      ) : (
                        <div className="border-2 border-primary/30 rounded-lg p-4 bg-white space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-brown-dark">
                              Nueva Hoja Interna
                            </h4>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setMostrarCreadorHoja(false);
                                setNuevaHoja({
                                  nombre: "",
                                  descripcion: "",
                                  orden: 0,
                                  archivo: null,
                                  preview: null,
                                });
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Nombre */}
                          <div>
                            <Label htmlFor="nombre-hoja-nueva">Nombre *</Label>
                            <Input
                              id="nombre-hoja-nueva"
                              value={nuevaHoja.nombre}
                              onChange={(e) =>
                                setNuevaHoja((prev) => ({
                                  ...prev,
                                  nombre: e.target.value,
                                }))
                              }
                              placeholder="Ej: Registro de Vacunas"
                              className="mt-1"
                            />
                          </div>

                          {/* Descripción */}
                          <div>
                            <Label htmlFor="descripcion-hoja-nueva">
                              Descripción (opcional)
                            </Label>
                            <Input
                              id="descripcion-hoja-nueva"
                              value={nuevaHoja.descripcion}
                              onChange={(e) =>
                                setNuevaHoja((prev) => ({
                                  ...prev,
                                  descripcion: e.target.value,
                                }))
                              }
                              placeholder="Breve descripción..."
                              className="mt-1"
                            />
                          </div>

                          {/* Upload de imagen */}
                          <div>
                            <Label>Imagen de la hoja *</Label>
                            {!nuevaHoja.preview ? (
                              <div className="mt-2">
                                <input
                                  type="file"
                                  accept="image/jpeg,image/jpg,image/png"
                                  onChange={handleArchivoHojaChange}
                                  className="hidden"
                                  id="archivo-hoja-nueva"
                                />
                                <label
                                  htmlFor="archivo-hoja-nueva"
                                  className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-primary transition-colors"
                                >
                                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                                  <p className="text-sm text-gray-600">
                                    Click para seleccionar imagen
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    JPG, PNG • Máx 5MB
                                  </p>
                                </label>
                              </div>
                            ) : (
                              <div className="relative mt-2 border-2 border-primary rounded-lg overflow-hidden">
                                <img
                                  src={nuevaHoja.preview}
                                  alt="Preview"
                                  className="w-full h-48 object-cover"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  onClick={() =>
                                    setNuevaHoja((prev) => ({
                                      ...prev,
                                      archivo: null,
                                      preview: null,
                                    }))
                                  }
                                  className="absolute top-2 right-2"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>

                          {/* Botón de crear */}
                          <Button
                            type="button"
                            onClick={handleCrearHoja}
                            disabled={
                              !nuevaHoja.archivo ||
                              !nuevaHoja.nombre ||
                              crearHojaMutation.isPending
                            }
                            className="w-full"
                          >
                            {crearHojaMutation.isPending ? (
                              <>
                                <Upload className="h-4 w-4 mr-2 animate-pulse" />
                                Creando...
                              </>
                            ) : (
                              <>
                                <Check className="h-4 w-4 mr-2" />
                                Crear Hoja
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto p-2 border rounded-lg bg-gray-50">
                      {hojasDisponibles.map((hoja: HojaInterna) => {
                        const isSelected =
                          formData.configuracionPersonalizable?.hojasDisponibles?.includes(
                            hoja.id
                          );
                        return (
                          <div
                            key={hoja.id}
                            onClick={() => handleHojaToggle(hoja.id)}
                            className={`
                              relative cursor-pointer rounded-lg border-2 overflow-hidden transition-all duration-200
                              ${
                                isSelected
                                  ? "border-primary bg-primary/5 shadow-md"
                                  : "border-gray-200 hover:border-gray-300 bg-white"
                              }
                            `}
                          >
                            {/* Checkbox indicator */}
                            <div
                              className={`
                              absolute top-2 right-2 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center
                              ${
                                isSelected
                                  ? "bg-primary border-primary"
                                  : "bg-white border-gray-300"
                              }
                            `}
                            >
                              {isSelected && (
                                <Check className="h-4 w-4 text-white" />
                              )}
                            </div>

                            {/* Imagen */}
                            <div className="aspect-[3/4] overflow-hidden bg-gray-100">
                              <img
                                src={hoja.path}
                                alt={hoja.nombre}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src =
                                    "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=300&h=400&fit=crop";
                                }}
                              />
                            </div>

                            {/* Nombre y descripción */}
                            <div className="p-2 bg-white">
                              <p className="text-xs font-medium text-gray-700 line-clamp-1 text-center">
                                {hoja.nombre}
                              </p>
                              {hoja.descripcion && (
                                <p className="text-[10px] text-gray-500 line-clamp-1 text-center mt-0.5">
                                  {hoja.descripcion}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {hojasDisponibles && hojasDisponibles.length > 0 && (
                    <>
                      <p className="text-xs text-muted-foreground mt-2">
                        💡 Haz clic en las hojas para seleccionar el contenido
                        que vendrá incluido en este producto
                      </p>

                      {/* Botón para agregar más hojas */}
                      {!mostrarCreadorHoja ? (
                        <Button
                          type="button"
                          onClick={() => setMostrarCreadorHoja(true)}
                          variant="outline"
                          size="sm"
                          className="w-full mt-3"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Agregar otra hoja a "{categoriaHojas}"
                        </Button>
                      ) : (
                        <div className="border-2 border-primary/30 rounded-lg p-4 bg-white space-y-4 mt-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-brown-dark">
                              Nueva Hoja Interna
                            </h4>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setMostrarCreadorHoja(false);
                                setNuevaHoja({
                                  nombre: "",
                                  descripcion: "",
                                  orden: 0,
                                  archivo: null,
                                  preview: null,
                                });
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Nombre */}
                          <div>
                            <Label htmlFor="nombre-hoja-nueva-2">
                              Nombre *
                            </Label>
                            <Input
                              id="nombre-hoja-nueva-2"
                              value={nuevaHoja.nombre}
                              onChange={(e) =>
                                setNuevaHoja((prev) => ({
                                  ...prev,
                                  nombre: e.target.value,
                                }))
                              }
                              placeholder="Ej: Registro de Vacunas"
                              className="mt-1"
                            />
                          </div>

                          {/* Descripción */}
                          <div>
                            <Label htmlFor="descripcion-hoja-nueva-2">
                              Descripción (opcional)
                            </Label>
                            <Input
                              id="descripcion-hoja-nueva-2"
                              value={nuevaHoja.descripcion}
                              onChange={(e) =>
                                setNuevaHoja((prev) => ({
                                  ...prev,
                                  descripcion: e.target.value,
                                }))
                              }
                              placeholder="Breve descripción..."
                              className="mt-1"
                            />
                          </div>

                          {/* Upload de imagen */}
                          <div>
                            <Label>Imagen de la hoja *</Label>
                            {!nuevaHoja.preview ? (
                              <div className="mt-2">
                                <input
                                  type="file"
                                  accept="image/jpeg,image/jpg,image/png"
                                  onChange={handleArchivoHojaChange}
                                  className="hidden"
                                  id="archivo-hoja-nueva-2"
                                />
                                <label
                                  htmlFor="archivo-hoja-nueva-2"
                                  className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-primary transition-colors"
                                >
                                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                                  <p className="text-sm text-gray-600">
                                    Click para seleccionar imagen
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    JPG, PNG • Máx 5MB
                                  </p>
                                </label>
                              </div>
                            ) : (
                              <div className="relative mt-2 border-2 border-primary rounded-lg overflow-hidden">
                                <img
                                  src={nuevaHoja.preview}
                                  alt="Preview"
                                  className="w-full h-48 object-cover"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  onClick={() =>
                                    setNuevaHoja((prev) => ({
                                      ...prev,
                                      archivo: null,
                                      preview: null,
                                    }))
                                  }
                                  className="absolute top-2 right-2"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>

                          {/* Botón de crear */}
                          <Button
                            type="button"
                            onClick={handleCrearHoja}
                            disabled={
                              !nuevaHoja.archivo ||
                              !nuevaHoja.nombre ||
                              crearHojaMutation.isPending
                            }
                            className="w-full"
                          >
                            {crearHojaMutation.isPending ? (
                              <>
                                <Upload className="h-4 w-4 mr-2 animate-pulse" />
                                Creando...
                              </>
                            ) : (
                              <>
                                <Check className="h-4 w-4 mr-2" />
                                Crear Hoja
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tamaños Estándar (para compra directa) */}
        {/* COMENTADO: No se usa actualmente - todos los productos son personalizables */}
        {/* {formData.type === "físico" && (
          <EditorTamanos
            titulo="📏 Tamaños Estándar (Compra Directa)"
            descripcion="Estos son los precios cuando el cliente hace click en 'Comprar' (sin personalización)"
            opciones={formData.opcionesTamano || []}
            onChange={(opciones) =>
              setFormData((prev) => ({ ...prev, opcionesTamano: opciones }))
            }
          />
        )} */}

        {/* Tamaños Personalizables (solo si es personalizable) */}
        {formData.type === "físico" && esPersonalizable && (
          <EditorTamanos
            titulo="📏 Tamaños y Precios del Producto (Opcional)"
            descripcion="Configura los tamaños disponibles y sus precios. Si no agregas tamaños, se usará el precio base del producto. Puedes agregar tamaños después de crear el producto."
            icono={<Palette className="h-5 w-5" />}
            opciones={
              formData.configuracionPersonalizable?.personalizacion
                ?.opcionesTamano || []
            }
            onChange={(opciones) =>
              setFormData((prev) => ({
                ...prev,
                configuracionPersonalizable: {
                  ...prev.configuracionPersonalizable,
                  personalizacion: {
                    ...prev.configuracionPersonalizable?.personalizacion,
                    opcionesTamano: opciones,
                  },
                },
              }))
            }
            mostrarComparacion={false}
          />
        )}

        {/* Configuración de Personalización */}
        {esPersonalizable && (
          <>
            {console.log('🔍 [DEBUG] formData.configuracionPersonalizable al renderizar EditorPersonalizacion:', formData.configuracionPersonalizable)}
            {console.log('🔍 [DEBUG] value que se pasa a EditorPersonalizacion:', formData.configuracionPersonalizable?.personalizacion)}
            <EditorPersonalizacion
              value={formData.configuracionPersonalizable?.personalizacion || {}}
              onChange={(personalizacion) => {
                console.log('🔍 [DEBUG] EditorPersonalizacion onChange llamado con:', personalizacion);
                setFormData((prev) => {
                  const updated = {
                    ...prev,
                    configuracionPersonalizable: {
                      ...prev.configuracionPersonalizable,
                      personalizacion: {
                        ...prev.configuracionPersonalizable?.personalizacion,
                        ...personalizacion
                      },
                    },
                  };
                  console.log('🔍 [DEBUG] Nuevo formData después de merge:', updated.configuracionPersonalizable);
                  return updated;
                });
              }}
            />
          </>
        )}

        {/* Descripción */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Descripción del Producto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Describe detalladamente el producto, sus características, beneficios, usos, etc... (opcional)"
              rows={4}
              className="text-base"
            />
          </CardContent>
        </Card>

        {/* Contenido multimedia */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Imágenes del producto */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Imágenes del Producto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Selector de orientación */}
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium whitespace-nowrap">Orientación:</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, orientacion: 'vertical' }))}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                      formData.orientacion !== 'horizontal'
                        ? 'border-primary bg-primary/10 font-medium'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="w-4 h-6 border-2 border-current rounded-sm" />
                    Vertical
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, orientacion: 'horizontal' }))}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                      formData.orientacion === 'horizontal'
                        ? 'border-primary bg-primary/10 font-medium'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="w-6 h-4 border-2 border-current rounded-sm" />
                    Horizontal
                  </button>
                </div>
              </div>

              <MultiImageUpload
                values={formData.images}
                onChange={(values) =>
                  setFormData((prev) => ({ ...prev, images: values }))
                }
                maxImages={10}
                orientacion={formData.orientacion}
              />
            </CardContent>
          </Card>

          {/* Archivo digital (solo para productos digitales) */}
          {/* {formData.type === "digital" && (
            <DigitalFileUpload
              onFileSelected={handleDigitalFileSelected}
              selectedFileId={formData.digitalFileId}
              disabled={isLoading}
            />
          )} */}

          {/* Mensaje informativo para productos físicos */}
          {formData.type === "físico" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Producto Físico
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">
                      Producto con inventario físico
                    </p>
                    <p>
                      Este producto requiere gestión de stock y puede necesitar
                      envío físico al cliente.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tags y metadatos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Tags y Categorización
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Agregar etiquetas para facilitar búsquedas..."
                className="text-base"
                onKeyPress={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addTag())
                }
              />
              <Button type="button" onClick={addTag} variant="outline">
                <Plus className="h-4 w-4 mr-1" />
                Agregar
              </Button>
            </div>

            {formData.tags.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Tags agregados:</p>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="cursor-pointer hover:bg-red-100 transition-colors px-3 py-1"
                      onClick={() => removeTag(tag)}
                    >
                      {tag} <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {formData.tags.length === 0 && (
              <p className="text-sm text-muted-foreground italic">
                Agrega tags para que los clientes puedan encontrar fácilmente
                este producto
              </p>
            )}
          </CardContent>
        </Card>

        {/* Botones de acción */}
        <div className="flex justify-between items-center pt-6 border-t">
          {/* <div className="text-sm text-muted-foreground">
            {formData.type === "digital" && !formData.digitalFileId && (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                <span>
                  Recuerda subir un archivo digital antes de crear el producto
                </span>
              </div>
            )}
          </div> */}

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              size="lg"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white min-w-[140px]"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {product ? "Actualizar" : "Crear"} Producto
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

const AdminProducts = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<ProductFilters>({
    search: "",
    type: "",
    isActive: true, // Por defecto muestra solo ACTIVOS (los inactivos se ocultan)
    page: 1,
    limit: 50, // Aumentar límite para ver más productos
  });

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);

  // Helper para convertir nombres de campos técnicos a nombres amigables
  const getFieldDisplayName = (field: string): string => {
    const fieldNames: Record<string, string> = {
      title: "Título",
      description: "Descripción",
      type: "Tipo de producto",
      price: "Precio",
      stock: "Stock",
      image: "Imagen principal",
      images: "Imágenes",
      tags: "Etiquetas",
      digitalFileId: "Archivo digital",
      isActive: "Estado activo",
    };
    return fieldNames[field] || field;
  };

  // Query para obtener productos
  const { data: productsData, isLoading } = useQuery({
    queryKey: ["admin-products", filters],
    queryFn: () => productService.getProducts(filters),
    staleTime: 2 * 60 * 1000,
  });

  // Query para obtener el producto completo al abrir el modal de edición
  // El listado omite algunos campos (ej: description) por optimización de payload
  const { data: fullProduct, isLoading: isLoadingFullProduct } = useQuery({
    queryKey: ["admin-product-detail", selectedProduct?._id],
    queryFn: () => productService.getProduct(selectedProduct!._id),
    enabled: !!selectedProduct?._id && isFormOpen,
    staleTime: 0,
  });

  // Mutación para crear producto
  const createMutation = useMutation({
    mutationFn: (data: ProductFormData) => productService.createProduct(data),
    onSuccess: (newProduct) => {
      // Invalidar queries para refrescar la lista
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });

      // Agregar el nuevo producto al cache optimistamente
      queryClient.setQueryData<Product[]>(
        ["admin-products", filters],
        (oldData) => {
          if (!oldData) return [newProduct];
          return [newProduct, ...oldData];
        }
      );

      setIsFormOpen(false);
      toast({
        title: "Producto creado",
        description: "El producto se ha creado exitosamente",
      });
    },
    onError: (error: unknown) => {
      let errorTitle = "Error al crear producto";
      let errorMessage = "Ocurrió un error inesperado";
      let errorDetails: string[] = [];

      // Log del error completo para debugging
      console.error("🔴 Error completo al crear producto:", error);

      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        const responseData = error.response.data as any;

        // Caso 1: Errores de validación detallados (formato del backend)
        if ("details" in responseData && Array.isArray(responseData.details)) {
          errorTitle = "Campos inválidos o faltantes";

          // Agrupar errores por campo para mejor legibilidad
          const fieldMessages: Record<string, string[]> = {};
          responseData.details.forEach((detail: any) => {
            const field = detail.field || "general";
            const message = detail.message || "Error desconocido";

            if (!fieldMessages[field]) {
              fieldMessages[field] = [];
            }
            fieldMessages[field].push(message);
          });

          // Convertir a formato legible
          errorDetails = Object.entries(fieldMessages).map(
            ([field, messages]) => {
              const fieldName = getFieldDisplayName(field);
              return `• ${fieldName}: ${messages.join(", ")}`;
            }
          );

          errorMessage =
            errorDetails.length === 1
              ? errorDetails[0].replace("• ", "")
              : `Se encontraron ${errorDetails.length} errores:`;

          console.error("📋 Detalles de validación:", responseData.details);
        }
        // Caso 2: Errores de imagen (del middleware de imágenes)
        else if (
          responseData.code === "IMAGE_VALIDATION_ERROR" &&
          responseData.details
        ) {
          errorTitle = "Error en las imágenes";
          errorDetails = responseData.details.map((detail: any) => {
            let msg = `• ${detail.message}`;
            if (detail.fileName) msg += ` (${detail.fileName})`;
            if (detail.currentResolution && detail.maxResolution) {
              msg += `\n  Resolución: ${detail.currentResolution} > ${detail.maxResolution}`;
            }
            if (detail.currentSize && detail.maxSize) {
              msg += `\n  Tamaño: ${detail.currentSize} > ${detail.maxSize}`;
            }
            return msg;
          });
          errorMessage = `Se encontraron ${errorDetails.length} error(es) en las imágenes`;
        }
        // Caso 3: Mensaje de error simple
        else if ("message" in responseData) {
          errorMessage = String(responseData.message);
        }
        // Caso 4: Error genérico con código
        else if ("error" in responseData) {
          errorMessage = String(responseData.error);
        }
      }

      // Mostrar el toast con el error principal
      toast({
        title: errorTitle,
        description:
          errorDetails.length > 0
            ? `${errorMessage}\n${errorDetails.join("\n")}`
            : errorMessage,
        variant: "destructive",
        duration: 6000, // Más tiempo para leer errores detallados
      });

      // Log adicional en desarrollo
      if (import.meta.env.DEV) {
        console.log("🔴 Mensaje de error mostrado:", errorMessage);
        if (errorDetails.length > 0) {
          console.log("📝 Detalles adicionales:", errorDetails);
        }
      }
    },
  });

  // Mutación para actualizar producto
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProductFormData }) =>
      productService.updateProduct(id, data),
    onSuccess: (updatedProduct) => {
      // Log para debugging
      console.log(
        "✅ Producto actualizado recibido del backend:",
        updatedProduct
      );
      console.log(
        "📊 featuredOrder en respuesta:",
        updatedProduct.featuredOrder
      );

      // Invalidar queries para refrescar la lista
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });

      // Actualizar el producto en el cache optimistamente
      queryClient.setQueryData<Product[]>(
        ["admin-products", filters],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((product) =>
            product._id === updatedProduct._id ? updatedProduct : product
          );
        }
      );

      setIsFormOpen(false);
      setSelectedProduct(null);
      toast({
        title: "Producto actualizado",
        description: "El producto se ha actualizado exitosamente",
      });
    },
    onError: (error: unknown) => {
      let errorTitle = "Error al actualizar producto";
      let errorMessage = "Ocurrió un error inesperado";
      let errorDetails: string[] = [];

      console.error("🔴 Error completo al actualizar producto:", error);

      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        const responseData = error.response.data as any;

        // Errores de validación detallados
        if ("details" in responseData && Array.isArray(responseData.details)) {
          errorTitle = "Campos inválidos";

          const fieldMessages: Record<string, string[]> = {};
          responseData.details.forEach((detail: any) => {
            const field = detail.field || "general";
            const message = detail.message || "Error desconocido";
            if (!fieldMessages[field]) {
              fieldMessages[field] = [];
            }
            fieldMessages[field].push(message);
          });

          errorDetails = Object.entries(fieldMessages).map(
            ([field, messages]) => {
              const fieldName = getFieldDisplayName(field);
              return `• ${fieldName}: ${messages.join(", ")}`;
            }
          );

          errorMessage =
            errorDetails.length === 1
              ? errorDetails[0].replace("• ", "")
              : `Se encontraron ${errorDetails.length} errores:`;
        }
        // Errores de imagen
        else if (
          responseData.code === "IMAGE_VALIDATION_ERROR" &&
          responseData.details
        ) {
          errorTitle = "Error en las imágenes";
          errorDetails = responseData.details.map((detail: any) => {
            let msg = `• ${detail.message}`;
            if (detail.fileName) msg += ` (${detail.fileName})`;
            return msg;
          });
          errorMessage = `Se encontraron ${errorDetails.length} error(es) en las imágenes`;
        } else if ("message" in responseData) {
          errorMessage = String(responseData.message);
        } else if ("error" in responseData) {
          errorMessage = String(responseData.error);
        }
      }

      toast({
        title: errorTitle,
        description:
          errorDetails.length > 0
            ? `${errorMessage}\n${errorDetails.join("\n")}`
            : errorMessage,
        variant: "destructive",
        duration: 6000,
      });
    },
  });

  // Mutación para eliminar producto
  const deleteMutation = useMutation({
    mutationFn: (id: string) => productService.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      toast({
        title: "Producto eliminado",
        description: "El producto se ha eliminado exitosamente",
      });
    },
    onError: (error: unknown) => {
      let errorMessage = "Error al eliminar el producto";
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "message" in error.response.data
      ) {
        errorMessage = String(error.response.data.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleFormSubmit = (data: ProductFormData) => {
    // Limpiar digitalFileId si está vacío para productos físicos
    const cleanedData = { ...data };
    if (cleanedData.type === "físico" || !cleanedData.digitalFileId) {
      delete cleanedData.digitalFileId;
    }

    // TEMPORAL: Enviar también 'image' para compatibilidad con backend antiguo
    // Usar la primera imagen del array como imagen principal
    if (cleanedData.images && cleanedData.images.length > 0) {
      cleanedData.image = cleanedData.images[0];
    }

    // Backend rechaza categoriaEstampados vacío — fallback defensivo antes de enviar
    if (
      cleanedData.configuracionPersonalizable?.personalizacion &&
      !cleanedData.configuracionPersonalizable.personalizacion.categoriaEstampados
    ) {
      cleanedData.configuracionPersonalizable = {
        ...cleanedData.configuracionPersonalizable,
        personalizacion: {
          ...cleanedData.configuracionPersonalizable.personalizacion,
          categoriaEstampados: "infantil",
        },
      };
    }

    console.log('📤 [DEBUG] Datos que se enviarán al backend:', cleanedData);
    console.log('📤 [DEBUG] configuracionPersonalizable que se enviará:', cleanedData.configuracionPersonalizable);
    console.log('📤 [DEBUG] personalizacion que se enviará:', cleanedData.configuracionPersonalizable?.personalizacion);

    if (selectedProduct) {
      updateMutation.mutate({ id: selectedProduct._id, data: cleanedData });
    } else {
      createMutation.mutate(cleanedData);
    }
  };

  const openCreateForm = () => {
    setSelectedProduct(null);
    setIsFormOpen(true);
  };

  const openEditForm = (product: Product) => {
    setSelectedProduct(product);
    setIsFormOpen(true);
  };

  const openView = (product: Product) => {
    setSelectedProduct(product);
    setIsViewOpen(true);
  };

  const getStockStatus = (product: Product) => {
    if (product.type === "digital") return null;
    if (product.stock === 0)
      return { label: "Sin stock", color: "destructive" };
    if (product.stock <= 5) return { label: "Stock bajo", color: "warning" };
    return { label: "En stock", color: "success" };
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const products = productsData || [];
  const totalProducts = products.length;
  const activeProducts = products.filter((p) => p.isActive).length;
  const lowStockProducts = products.filter(
    (p) => p.type === "físico" && p.stock <= 5
  ).length;
  const outOfStockProducts = products.filter(
    (p) => p.type === "físico" && p.stock === 0
  ).length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Gestión de Productos
          </h1>
          <p className="text-muted-foreground">
            Administra el catálogo de productos de tu tienda
          </p>
        </div>
        <Button onClick={openCreateForm} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nuevo Producto
        </Button>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Productos
            </CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalProducts}</div>
            <p className="text-xs text-muted-foreground">
              {activeProducts} activos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Stock Bajo</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{lowStockProducts}</div>
            <p className="text-xs text-muted-foreground">≤ 5 unidades</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sin Stock</CardTitle>
            <TrendingUp className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{outOfStockProducts}</div>
            <p className="text-xs text-muted-foreground">
              Necesitan reposición
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Valor Inventario
            </CardTitle>
            <DollarSign className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(
                products.reduce((acc, p) => acc + p.price * (p.stock || 1), 0)
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Valor total estimado
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Buscar productos..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: e.target.value,
                      page: 1,
                    }))
                  }
                  className="pl-10"
                />
              </div>
            </div>

            <Select
              value={filters.type || "all"}
              onValueChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  type: value === "all" ? "" : (value as "físico" | "digital" | "taller"),
                  page: 1,
                }))
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="físico">Físico</SelectItem>
                <SelectItem value="digital">Digital</SelectItem>
                <SelectItem value="taller">Taller</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={
                filters.isActive === ""
                  ? "all"
                  : filters.isActive === true
                  ? "true"
                  : "false"
              }
              onValueChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  isActive: value === "all" ? "" : value === "true",
                  page: 1,
                }))
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="true">Activos</SelectItem>
                <SelectItem value="false">Inactivos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de productos */}
      <Card>
        <CardHeader>
          <CardTitle>Productos ({totalProducts})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Producto</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Precio</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => {
                const stockStatus = getStockStatus(product);
                return (
                  <TableRow key={product._id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <img
                          src={product.images?.[0] || product.image}
                          alt={product.title}
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                        <div>
                          <div className="font-medium">{product.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {product?.tags?.slice(0, 2).map((tag) => (
                              <Badge
                                key={tag}
                                variant="outline"
                                className="mr-1 text-xs"
                              >
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          product.type === "físico" ? "default" : product.type === "taller" ? "outline" : "secondary"
                        }
                        className={product.type === "taller" ? "border-amber-500 text-amber-700" : ""}
                      >
                        {product.type === "taller" ? "🎨 taller" : product.type}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatCurrency(product.price)}</TableCell>
                    <TableCell>
                      {product.type === "digital" || product.type === "taller" ? (
                        <Badge variant="outline">{product.type === "taller" ? "–" : "∞"}</Badge>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span>{product.stock}</span>
                          {stockStatus && (
                            <Badge
                              variant={
                                stockStatus.color as
                                  | "default"
                                  | "secondary"
                                  | "destructive"
                                  | "outline"
                              }
                              className="text-xs"
                            >
                              {stockStatus.label}
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={product.isActive ? "default" : "secondary"}
                          className={
                            product.isActive
                              ? "bg-green-100 text-green-800 border-green-300"
                              : "bg-red-100 text-red-800 border-red-300"
                          }
                        >
                          {product.isActive ? "✓ Activo" : "✕ Inactivo"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openView(product)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditForm(product)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                ¿Eliminar producto?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción desactivará el producto "
                                {product.title}". No se eliminará
                                permanentemente, pero no será visible para los
                                clientes.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() =>
                                  deleteMutation.mutate(product._id)
                                }
                                className="bg-red-600 hover:bg-red-700 text-white"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal de formulario */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto">
          <DialogHeader className="sr-only">
            <DialogTitle>
              {selectedProduct ? "Editar Producto" : "Crear Nuevo Producto"}
            </DialogTitle>
            <DialogDescription>
              {selectedProduct
                ? "Modifica los datos del producto seleccionado"
                : "Completa la información para crear un nuevo producto"}
            </DialogDescription>
          </DialogHeader>
          <ProductForm
            product={selectedProduct ? (fullProduct ?? selectedProduct) : null}
            onSubmit={handleFormSubmit}
            onCancel={() => setIsFormOpen(false)}
            isLoading={createMutation.isPending || updateMutation.isPending || (!!selectedProduct && isLoadingFullProduct)}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de vista */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalles del Producto</DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4">
              <img
                src={selectedProduct.images?.[0] || selectedProduct.image}
                alt={selectedProduct.title}
                className="w-full h-64 object-cover rounded-lg"
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold">Título</h3>
                  <p>{selectedProduct.title}</p>
                </div>
                <div>
                  <h3 className="font-semibold">Tipo</h3>
                  <Badge
                    variant={
                      selectedProduct.type === "físico"
                        ? "default"
                        : "secondary"
                    }
                  >
                    {selectedProduct.type}
                  </Badge>
                </div>
                <div>
                  <h3 className="font-semibold">Precio</h3>
                  <p>{formatCurrency(selectedProduct.price)}</p>
                </div>
                {selectedProduct.type === "físico" && (
                  <div>
                    <h3 className="font-semibold">Stock</h3>
                    <p>{selectedProduct.stock}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <h3 className="font-semibold">Tags</h3>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedProduct?.tags?.map((tag) => (
                      <Badge key={tag} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
